import { merge } from "lodash";
import { v4 as uuid } from "uuid";
import {
  ELEVATION_DATUM,
  OBJECT_TYPE,
  PROPERTY_OBJECT_LABEL,
  UserFriendlyCertificateStatus,
} from "common/constants";
import {
  formValueDecodeObject,
  FormValueEncoded,
  isFormValueEncoded,
} from "common/utils/customObjects";
import {
  FILTER_CONDITIONAL_TO_QUERY_OPERATOR,
  FILTER_CONDITIONALS,
  FilterDescription,
  OPERATOR_TO_CONDITIONAL,
  QueryDescription,
  SUBMISSION_TABLE_TO_CATEGORY,
  TABLE_NAMES,
} from "common/utils/queryBuilder";
import {
  formatBoolean,
  formatBuildingElevationSource,
  formatDatum,
  formatECReviewStatus,
} from "common/utils/strings";
import { decorateSubmissionTypeNameWithIntakeSource } from "common/utils/submissions";
import {
  CertificateUploadStatus,
  SubmissionType,
} from "../../../../../generated/graphql";
import { Account } from "../../../../Authorization/types";
import { Attribute, isAttributeLeaf, isAttributeNode } from "../../types";
import {
  buildFilterSegmentForFilter,
  FilterReducerState,
} from "./useFilterReducer";

const commonLabelHelper = ({ value, lastElement }: ValueHelperArgs) => {
  return (
    lastElement.options?.find(option => option.value === value)?.label ?? null
  );
};

const commonCustomMapHelper = ({ value, account }: ValueHelperArgs) => {
  return isFormValueEncoded(value) ?
      objectTypeFromEncodedValue({
        customMaps: account?.customMaps ?? [],
        encodedValue: value,
      })
    : null;
};

const submissionTypeNameFromId = ({
  submissionTypes,
  id,
}: {
  submissionTypes: Array<
    Pick<SubmissionType, "id" | "name" | "modules" | "canCreate">
  >;
  id: string;
}) => {
  const submissionType = submissionTypes.find(st => st.id === id) ?? null;

  return submissionType ?
      decorateSubmissionTypeNameWithIntakeSource({
        modules: submissionType.modules,
        submissionTypeName: submissionType.name,
        canCreate: submissionType.canCreate,
      })
    : null;
};

const objectTypeFromEncodedValue = ({
  customMaps,
  encodedValue,
}: {
  customMaps: Account["customMaps"];
  encodedValue: FormValueEncoded;
}) => {
  const formValueDecoded = formValueDecodeObject(encodedValue);
  if (formValueDecoded.type === OBJECT_TYPE.PROPERTY) {
    return PROPERTY_OBJECT_LABEL;
  } else {
    return (
      customMaps.find(cm => cm.id === formValueDecoded.customMapId)?.name ??
      null
    );
  }
};

type ValueHelperArgs = {
  account: Maybe<Account>;
  value: Maybe<string | number>;
  lastElement: Attribute;
};

const FILTER_STATE_VALUE_HELPERS: Record<
  string,
  (args: ValueHelperArgs) => Maybe<string | number>
> = merge(
  {
    "Properties.warnings": ({ account, value }: ValueHelperArgs) => {
      if (!account) return null;

      return (
        account.accountPropertyWarningDefinitions.find(
          apwd => apwd.id === value,
        )?.title ?? null
      );
    },

    "Permits.ecReviewStatus": ({ value }: ValueHelperArgs) => {
      return formatECReviewStatus(value as string);
    },

    "Files.issues": ({ value, lastElement, account }: ValueHelperArgs) => {
      return commonLabelHelper({ value, lastElement, account });
    },

    "Certificates.buildingElevationSource": ({ value }: ValueHelperArgs) => {
      return formatBuildingElevationSource(value as string);
    },

    "CertificateUploads.status": ({ value }: ValueHelperArgs) => {
      return UserFriendlyCertificateStatus[value as CertificateUploadStatus];
    },

    "Files.objectType": ({ value, account, lastElement }: ValueHelperArgs) => {
      return commonCustomMapHelper({ value, account, lastElement });
    },
    "CustomMaps.name": ({ value, account, lastElement }: ValueHelperArgs) => {
      return commonCustomMapHelper({ value, account, lastElement });
    },
    "Tasks.assignees": ({ value, lastElement, account }: ValueHelperArgs) => {
      return commonLabelHelper({ value, lastElement, account });
    },
    "Tasks.objectType": ({ value, account, lastElement }: ValueHelperArgs) => {
      return commonCustomMapHelper({ value, account, lastElement });
    },
  },
  // this creates a helper function for each submission table's name
  ...Object.keys(SUBMISSION_TABLE_TO_CATEGORY).map(table => ({
    [`${table}.submissionTypeName`]: ({ account, value }: ValueHelperArgs) => {
      if (!account) return null;

      return submissionTypeNameFromId({
        submissionTypes: account.submissionTypes,
        id: value as string,
      });
    },
    [`${table}.objectType`]: ({
      value,
      account,
      lastElement,
    }: ValueHelperArgs) => {
      return commonCustomMapHelper({ value, account, lastElement });
    },
  })),
);

export const getAllFilterableAttributes = (
  columnConfig: Array<Attribute>,
): Array<Attribute> => {
  const filterableAttributes = columnConfig.reduce<Array<Attribute>>(
    (acc, attribute) => {
      if (attribute.omitFromFilters) {
        return acc;
      } else if ("attributes" in attribute) {
        return [
          ...acc,
          {
            ...attribute,
            attributes: getAllFilterableAttributes(attribute.attributes),
          },
        ];
      } else {
        return [...acc, attribute];
      }
    },
    [],
  );

  return filterableAttributes;
};

export const queryDescriptionToFilterState = ({
  queryDescription,
  filterConfig,
  account,
  filterExclusionMatcher,
}: {
  queryDescription: QueryDescription;
  filterConfig: Array<Attribute>;
  account: Maybe<Account>;
  filterExclusionMatcher?: (filterDescription: FilterDescription) => boolean;
}) => {
  const filterState: Pick<FilterReducerState, "filterValues"> = {
    filterValues: {},
  };

  // Filter out excluded filters before processing
  const filtersToProcess = (queryDescription.filters || []).filter(filter => {
    return !filterExclusionMatcher?.(filter);
  });

  filtersToProcess.forEach(filter => {
    const key = `${filter.table}.${filter.field}`;
    const attributeHistory =
      findPathByColumnId(filterConfig, key, filter.value) ?? [];

    if (attributeHistory.length === 0) {
      return;
    }

    const lastElement = [...attributeHistory].pop()!;

    const isEnumField =
      isAttributeLeaf(lastElement) && lastElement.type === "enum";

    const isDateField =
      isAttributeLeaf(lastElement) && lastElement.type === "date";

    const isGroupField =
      "isGroup" in lastElement && lastElement.isGroup === true;

    const isBooleanField =
      isAttributeLeaf(lastElement) &&
      lastElement.type === "enum" &&
      lastElement.options?.every(
        option => option.value === "true" || option.value === "false",
      ) &&
      lastElement.options.length === 2;

    const isDatumField =
      isAttributeLeaf(lastElement) &&
      lastElement.type === "enum" &&
      lastElement.options?.every(option =>
        Object.values(ELEVATION_DATUM).includes(
          option.value as ELEVATION_DATUM,
        ),
      );

    const uniqueId = uuid();

    let presentationalValue =
      FILTER_STATE_VALUE_HELPERS[key]?.({
        account,
        value: filter.value,
        lastElement,
      }) ?? (isGroupField && !isEnumField ? null : filter.value);

    if (isBooleanField) {
      presentationalValue = formatBoolean(filter.value === "true");
    }

    if (isDatumField) {
      presentationalValue = formatDatum(filter.value as ELEVATION_DATUM);
    }

    if (filterState.filterValues?.[key]) {
      filterState.filterValues[key]!.push(
        buildFilterForFilterState({
          filter,
          uniqueId,
          presentationalValue: presentationalValue?.toString() ?? null,
          key,
          filterConfig,
          isDateField,
        }),
      );
    } else {
      filterState.filterValues = {
        ...filterState.filterValues,
        [key]: [
          buildFilterForFilterState({
            filter,
            uniqueId,
            presentationalValue: presentationalValue?.toString() ?? null,
            key,
            filterConfig,
            isDateField,
          }),
        ],
      };
    }
  });

  return filterState;
};

const buildFilterForFilterState = ({
  filter,
  uniqueId,
  presentationalValue,
  key,
  filterConfig,
  isDateField,
}: {
  filter: Omit<FilterDescription, "value"> & {
    value: any;
  };
  uniqueId: string;
  presentationalValue: Maybe<string>;
  key: string;
  filterConfig: Array<Attribute>;
  isDateField?: boolean;
}) => {
  let conditional = OPERATOR_TO_CONDITIONAL[filter.operator];

  //we need to convert greater than and less than date operators to the correct display conditionals
  if (isDateField) {
    if (conditional === FILTER_CONDITIONALS.IS_GREATHER_THAN) {
      conditional = FILTER_CONDITIONALS.AFTER;
    }
    if (conditional === FILTER_CONDITIONALS.IS_LESS_THAN) {
      conditional = FILTER_CONDITIONALS.BEFORE;
    }
  }
  return {
    value: filter.value,
    conditional: OPERATOR_TO_CONDITIONAL[filter.operator],
    uniqueId,
    filterSegment: buildFilterSegmentForFilter({
      attributeHistory:
        findPathByColumnId(filterConfig, key, filter.value) ?? [],
      conditional,
      label: presentationalValue?.toString(),
      context: { key, uniqueId },
    }),
  };
};

const findPathByColumnId = (
  data: Array<Attribute>,
  columnId: string,
  value: Maybe<string | number>,
  path: Array<Attribute> = [],
): Maybe<Array<Attribute>> => {
  for (const item of data) {
    const currentPath = [...path, item];

    if (
      item.columnId === columnId ||
      // This narrows down options nodes to the correct value.
      // They'll be a leaf associated with a column, but there will be many of them, where the value is the tiebreaker
      (isAttributeLeaf(item) && item.key === columnId && value === item.value)
    ) {
      return currentPath;
    }

    if (isAttributeNode(item)) {
      const result = findPathByColumnId(
        item.attributes,
        columnId,
        value,
        currentPath,
      );
      if (result) return result;
    }
  }
  return null;
};

export const buildFilterDescriptionFromFilterStateObject = ({
  field,
  filter,
}: {
  field: string;
  filter: { value: Maybe<string>; conditional: FILTER_CONDITIONALS };
}): FilterDescription => {
  const splitField = field.split(".");

  return {
    table: splitField[0] as TABLE_NAMES,
    field: splitField[1]!,
    operator: FILTER_CONDITIONAL_TO_QUERY_OPERATOR[filter.conditional],
    value: filter.value,
  };
};
